// src/shared/sections.js
//
// Where a template section lives inside its product.
//
// A product does not keep all its sections in one list. It has three buckets:
//   • product.sections[]              — the agreement body, always present
//   • product.hostingSections[]       — inserted when hosting is selected
//   • product.udsSections[category][] — inserted per category when UDS is selected
//
// Pricing tables appear in all three (CQMsolution has one in each), but for a
// long time only the first bucket could be edited: the preview's "Edit table"
// button and PUT /api/products/:id/sections/:idx both indexed product.sections
// directly, so a hosting or UDS table had no path back to the template.
//
// A "locator" names the bucket and the index within it. The client derives one
// from the _src* tags it stamped onto the composed section; the server resolves
// it back to the live array. Both halves live here so they can't drift apart.
//
// CommonJS + dependency-free like the rest of src/shared — the Node server
// require()s it and the CRA client imports it.

// Resolve a locator to the actual array it points into (a live reference, so
// writing to it writes to the product). Returns null when the bucket does not
// exist on this product, or when subType is something we don't know — better to
// 404 than to guess and write a pricing table into the wrong place.
function sectionListFor(product, locator) {
  if (!product || !locator) return null;
  const { subType, subCategory } = locator;
  if (!subType) return Array.isArray(product.sections) ? product.sections : null;
  if (subType === 'hosting') {
    return Array.isArray(product.hostingSections) ? product.hostingSections : null;
  }
  if (subType === 'uds') {
    // Legacy templates carry udsSections as a flat array; only the
    // category-keyed object form is addressable.
    if (!product.udsSections || Array.isArray(product.udsSections)) return null;
    const list = product.udsSections[subCategory];
    return Array.isArray(list) ? list : null;
  }
  return null;
}

// The composed section the preview renders carries _src* tags saying where it
// came from. Turn those into a locator, or null for sections that have nowhere
// to save back to (custom/one-off inserts, library drops).
function templateTableLocator(section) {
  if (!section || !section._srcProductId) return null;
  const productId = section._srcProductId;
  const subType = section._srcSubType;
  const isIdx = (n) => Number.isInteger(n) && n >= 0;

  if (!subType) {
    return isIdx(section._srcSectionIdx) ? { productId, sectionIdx: section._srcSectionIdx } : null;
  }
  if (subType === 'hosting') {
    return isIdx(section._srcSubIdx) ? { productId, subType, sectionIdx: section._srcSubIdx } : null;
  }
  if (subType === 'uds') {
    return isIdx(section._srcSubIdx) && section._srcSubCategory
      ? { productId, subType, subCategory: section._srcSubCategory, sectionIdx: section._srcSubIdx }
      : null;
  }
  return null;
}

// The wire form of a locator's bucket half — what the client PUTs alongside the
// rows. The index travels in the URL, so it is deliberately not included.
function subFromLocator(locator) {
  if (!locator || !locator.subType) return undefined;
  return locator.subType === 'uds'
    ? { type: 'uds', category: locator.subCategory }
    : { type: locator.subType };
}

module.exports = { sectionListFor, templateTableLocator, subFromLocator };
